import type { QueryClient } from "@tanstack/react-query";
import { manageCubesQueryKeys } from "./manageCubes/queryKeys";
import {
  cubeYearsQueryKeys,
  forecastBudgetVersionsQueryKeys,
  forecastContextOptionsQueryKeys,
  forecastContextSelectorQueryKeys,
  forecastVersionsQueryKeys
} from "./queryKeys";

/**
 * Scope describing which client-side caches a cube mutation invalidates.
 *
 * - ``versions``: the mutation can add, remove, rename, or re-freeze cubes
 *   in the user / budget version dropdowns (create, delete, freeze).
 * - ``lifecycle``: the mutation changes a cube's internal data, years,
 *   week window, or context (trim, clone-brand, add/rename dim,
 *   copy-year, time-travel).
 *
 * Both are independent — a mutation may set either, both, or neither
 * (neither being useful when the caller only wants to gate on a custom
 * key).
 */
export interface CubeMutationScope {
  versions?: boolean;
  lifecycle?: boolean;
}

/**
 * Fan-out invalidation for cube mutations.
 *
 * Every cube mutation on the server clears the matching Polars caches
 * (see ``clear_polars_cache`` call sites in
 * ``appretio-api/appretio/_legacy/adapter/*``). On the client we mirror
 * that with a single source of truth for the query keys that depend on
 * cube state — so a new hook can't silently forget one.
 *
 * The returned promise resolves once TanStack Query has refetched the
 * affected queries. Callers should ``await`` it in their mutation
 * ``onSuccess`` so ``isPending`` stays true until the cache has caught
 * up (see CLAUDE.md "Mutation cache sync").
 */
export async function invalidateCubeMutation(
  queryClient: QueryClient,
  scope: CubeMutationScope
): Promise<void> {
  const pending: Promise<unknown>[] = [];

  if (scope.versions) {
    pending.push(
      queryClient.invalidateQueries({
        queryKey: forecastVersionsQueryKeys.all
      }),
      queryClient.invalidateQueries({
        queryKey: forecastBudgetVersionsQueryKeys.all
      })
    );
  }

  if (scope.lifecycle) {
    pending.push(
      queryClient.invalidateQueries({
        queryKey: manageCubesQueryKeys.all
      }),
      queryClient.invalidateQueries({
        queryKey: cubeYearsQueryKeys.all
      }),
      queryClient.invalidateQueries({
        queryKey: forecastContextOptionsQueryKeys.all
      }),
      // Lifecycle ops (clone-brand, time-travel, copy-year) can shift a
      // cube's year range, week window, or metadata labels. Both version
      // lists carry those: the dropdown labels and the ``CubeWindow`` the
      // grid gates edits on.
      queryClient.invalidateQueries({
        queryKey: forecastVersionsQueryKeys.all
      }),
      queryClient.invalidateQueries({
        queryKey: forecastBudgetVersionsQueryKeys.all
      }),
      // ``resetQueries`` wipes the cache so the context selector
      // re-fetches from scratch rather than briefly showing a stale
      // list while the refetch is in flight.
      queryClient.resetQueries({
        queryKey: forecastContextSelectorQueryKeys.all
      })
    );
  }

  await Promise.all(pending);
}
